import styled from '@emotion/styled';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Configure, Index, useHits, usePagination } from 'react-instantsearch';
import { useInView } from 'react-intersection-observer';
import { AlgoliaSearchTracker } from '~/services/instantsearch/components';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { EventIdentifier, useEvents } from '~/shared/hooks/useEvents';
import { _transformProductDetailsToDataLayer, getListName } from '~/shared/hooks/useEvents/helpers';
import { GTM_LIST_ID } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { hasGiftCardRuleContext } from '~/services/instantsearch';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { useFrame } from '~/shared/utils';
import { usePage } from '~/templates/pages';
import useCustomer from '../commerce-api/hooks/useCustomer';
import { CompareModeToggle } from './components/CompareModeToggle';
import SelectedFacets from './components/FilterBar/SelectedFacets/SelectedFacets';
import FilterDrawer from './components/FilterDrawer/FilterDrawer';
import { StoreAvailabilityInlineLink } from './components/StoreAvailability';
import FilterList from './components/FilterList/FilterList';
import PaginatedHits from './components/PaginatedHits/PaginatedHits';
import PLPSchema from './components/PLPSchema';
import { SSRPagination } from './components/Pagination/SSRPagination';
import { VirtualFacets, VirtualSearch } from './components/VirtualWidgets';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import {
    ALGOLIA_INDEX_ID_PRODUCT_LIST,
    ALGOLIA_INDEX_MAIN,
} from '~/services/instantsearch/variables';
import { ModuleContainerProps } from '~/templates/blocks/components/ModuleContainer/ModuleContainer';
import styles from './ProductsLoader.module.scss';

type Props = Pick<ModuleContainerProps, 'spacingTop'> & {
    hitsPerPage?: number;
    listId: EventIdentifier;
    configuration?: Record<string, any>;
    serverUrl?: string;
    /** Product list pages only: renders the PLP JSON-LD for the hits shown below. */
    withSchema?: boolean;
};

const ProductsListFilters = styled.div(() => ({
    marginBottom: 20,
}));

const ProductsLoader = ({
    hitsPerPage = 20,
    listId,
    configuration,
    serverUrl,
    withSchema,
    spacingTop,
}: Props) => {
    const { breadcrumb } = usePage();
    const { isPlusMember } = useCustomer();
    const isMobile = useIsMobile();
    const { data: frame } = useFrame();
    const { productsImpressionsEvent } = useEvents(frame);
    const { hits } = useHits<TProductDetails>();
    const { currentRefinement: currentPage } = usePagination();

    const { inView, ref } = useInView({
        threshold: 1,
        rootMargin: '0px 0px -500px 0px',
        triggerOnce: true,
    });

    const filterBarWrapperRef = useRef<HTMLDivElement>(null);
    const productListRef = useRef<HTMLDivElement>(null);

    const [isEventTriggered, setIsEventTriggered] = useState(false);

    const isGiftCardsList = useMemo(
        () => hasGiftCardRuleContext(configuration?.ruleContexts),
        [configuration?.ruleContexts],
    );

    /**
     * We will show the product facets list only if the product list is not for gift cards.
     */
    const shouldShowFacets = !isGiftCardsList;

    // Store-availability toggles apply to real product listings (category or search), not gift cards.
    const isCategoryPage =
        (listId === GTM_LIST_ID.PRODUCT_LIST || listId === GTM_LIST_ID.SEARCH_RESULTS) &&
        !isGiftCardsList;

    useEffect(() => {
        if (isEventTriggered) return;

        if (hits.length && inView) {
            const eventType =
                listId === GTM_LIST_ID.SEARCH_RESULTS ? listId : GTM_LIST_ID.PRODUCT_LIST;
            const itemListName = breadcrumb ? getListName(breadcrumb) : '';
            const transformedItems = hits.map((hit, index) =>
                _transformProductDetailsToDataLayer(hit, currentPage, index, isPlusMember),
            );

            productsImpressionsEvent(eventType, itemListName, transformedItems);
            setIsEventTriggered(true);
        }
    }, [hits, isPlusMember, inView]);

    return (
        <ModuleContainer spacingTop={spacingTop} ref={ref} id="plpFacets">
            <Index indexName={ALGOLIA_INDEX_MAIN} indexId={ALGOLIA_INDEX_ID_PRODUCT_LIST}>
                <Configure {...configuration} hitsPerPage={hitsPerPage} getRankingInfo />

                {withSchema && <PLPSchema />}

                <VirtualFacets />
                <AlgoliaSearchTracker />
                <VirtualSearch />

                <ProductsListFilters>
                    {isMobile && (
                        <>
                            <div className={styles.mobileToolbar}>
                                <FilterDrawer
                                    productListRef={productListRef}
                                    showStoreAvailability={isCategoryPage}
                                />
                                <CompareModeToggle />
                            </div>

                            {isCategoryPage && <StoreAvailabilityInlineLink />}
                        </>
                    )}

                    {shouldShowFacets && (
                        <div ref={filterBarWrapperRef}>
                            <FilterList />
                            <SelectedFacets />
                        </div>
                    )}
                </ProductsListFilters>

                <PaginatedHits
                    listId={listId}
                    itemsPerPage={hitsPerPage}
                    filterBarRef={filterBarWrapperRef}
                    productListRef={productListRef}
                    showStoreAvailability={isCategoryPage}
                />
            </Index>

            <SSRPagination asPath={serverUrl} />
        </ModuleContainer>
    );
};

export default ProductsLoader;
